import {
  cloneElement,
  forwardRef,
  isValidElement,
  PropsWithChildren,
  ReactNode,
} from "react";
import { TooltipTriggerProps } from "@radix-ui/react-tooltip";
import { TooltipProvider } from "@/providers/Tooltip.provider";
import {
  TooltipTrigger,
  Tooltip as TooltipRoot,
  TooltipContent,
} from "@/components/Base/Tooltip";

export type TooltipProps = PropsWithChildren & {
  message?: ReactNode;
  asChild?: boolean;
  tooltipTriggerProps?: TooltipTriggerProps;
  delayDuration?: number;
  disableHoverableContent?: boolean;
  className?: string;
};
export const Tooltip = forwardRef<HTMLButtonElement, TooltipProps>(
  (
    {
      message,
      children,
      asChild = true,
      tooltipTriggerProps,
      delayDuration,
      disableHoverableContent,
      className,
    },
    ref
  ) => {
    const wrapDisabledElement = (child: ReactNode) => {
      // Check if the child is a valid React element
      if (isValidElement(child)) {
        const isDisabledButton =
          (child.type === "button" || child.props?.disabled !== undefined) &&
          child.props?.disabled === true;

        // If the child is a disabled button, wrap it with a non-interactive element (span)
        if (isDisabledButton) {
          return <div>{cloneElement(child)}</div>;
        }
      }

      // Return original child if no wrapping is needed
      return child;
    };

    const wrappedChildren = wrapDisabledElement(children);

    // Only use asChild if we're not wrapping in a div and the child is a valid element
    const useAsChild = asChild && isValidElement(wrappedChildren);

    return (
      <TooltipProvider
        disableHoverableContent={disableHoverableContent}
        delayDuration={delayDuration ?? 700}
      >
        <TooltipRoot>
          <TooltipTrigger
            ref={ref}
            asChild={useAsChild}
            {...tooltipTriggerProps}
          >
            {wrappedChildren}
          </TooltipTrigger>
          <TooltipContent className={className}>{message}</TooltipContent>
        </TooltipRoot>
      </TooltipProvider>
    );
  }
);

Tooltip.displayName = "Tooltip";
